const FONT_STACKS = {
  Arial: 'Arial, Helvetica, sans-serif',
  Roboto: 'Roboto, Arial, Helvetica, sans-serif',
  'Segoe UI': "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif",
  Inter: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif',
};

const HEADER_PADDING = {
  compact: '35px 40px',
  medium: '52px 40px',
  spacious: '69px 40px',
};

const AUTO_FOOTER_MESSAGE =
  'Este e-mail foi enviado automaticamente. Por favor, não responda diretamente a esta mensagem.';

const EMAIL_CONTAINER_RADIUS = '20px';

function emailShellStyle(extra = '') {
  return [
    'width:100%',
    'max-width:560px',
    'margin:0 auto',
    `border-radius:${EMAIL_CONTAINER_RADIUS}`,
    '-webkit-border-radius:' + EMAIL_CONTAINER_RADIUS,
    'overflow:hidden',
    'background:#FFFFFF',
    'box-shadow:0 1px 2px rgba(15,23,42,0.04), 0 16px 40px rgba(15,23,42,0.08)',
    extra,
  ]
    .filter(Boolean)
    .join(';');
}

function normalizeHeaderLogoScale(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return 100;
  const stepped = Math.round(n / 10) * 10;
  return Math.max(10, Math.min(100, stepped));
}

function scaledHeaderPadding(headerSize, logoScalePercent) {
  const base = HEADER_PADDING[headerSize] || HEADER_PADDING.medium;
  const parts = base.split(/\s+/);
  const v = parseInt(parts[0], 10) || 52;
  const h = parseInt(parts[1], 10) || 40;
  const factor = normalizeHeaderLogoScale(logoScalePercent) / 100;
  const sv = Math.max(4, Math.round(v * factor));
  const sh = Math.max(12, Math.round(h * (0.7 + 0.3 * factor)));
  return `${sv}px ${sh}px`;
}

function hexToRgb(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  if (!/^[0-9A-Fa-f]{6}$/.test(raw)) return { r: 79, g: 70, b: 229 };
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
}

function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

function headerTextColor(primary, secondary) {
  const p = hexToRgb(primary);
  const s = hexToRgb(secondary);
  const luminance =
    (0.299 * (p.r + s.r) * 0.5 + 0.587 * (p.g + s.g) * 0.5 + 0.114 * (p.b + s.b) * 0.5) / 255;
  return luminance > 0.62 ? '#0F172A' : '#FFFFFF';
}

function logoMargin(align) {
  if (align === 'center') return '0 auto';
  if (align === 'right') return '0 0 0 auto';
  return '0';
}

function buildLogoBlock(visual, align, headerText) {
  const margin = logoMargin(align);
  const companyName = String(visual.headerCompanyName ?? '').trim() || 'Sua Empresa';

  if (String(visual.headerCompanyName ?? '').trim()) {
    return `<span style="display:inline-block;max-width:100%;font-size:20px;font-weight:700;letter-spacing:-0.01em;line-height:1.35;color:${headerText};">${escapeHtml(companyName)}</span>`;
  }

  if (visual.logoUrl) {
    return `<img src="${escapeHtml(visual.logoUrl)}" alt="${escapeHtml(companyName)}" width="140" style="display:block;max-width:140px;width:140px;height:auto;margin:${margin};border:0;line-height:100%;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;" />`;
  }

  return `<span style="display:inline-block;max-width:100%;font-size:20px;font-weight:700;letter-spacing:-0.01em;line-height:1.35;color:${headerText};">${escapeHtml(companyName)}</span>`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function replacePlaceholders(text, data) {
  return String(text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => {
    if (Object.prototype.hasOwnProperty.call(data, key)) return String(data[key]);
    return `{{${key}}}`;
  });
}

function bodyToHtml(text) {
  return escapeHtml(text).replace(/\n/g, '<br />');
}

function pickField(sampleData, key) {
  return replacePlaceholders(`{{${key}}}`, sampleData);
}

function buildPreviewText(sampleData) {
  const multiOrders = Array.isArray(sampleData.orders) && sampleData.orders.length > 1
    ? sampleData.orders
    : null;
  if (multiOrders) {
    const nums = multiOrders.map((o) => o.numero_os).filter((n) => n && n !== '—').join(', ');
    const tecnico = multiOrders[0]?.tecnico || '—';
    return `${tecnico} concluiu suas ordens de serviço ${nums}. Leva menos de 1 minuto para avaliar.`;
  }
  const tecnico = pickField(sampleData, 'tecnico');
  const numeroOs = pickField(sampleData, 'numero_os');
  return `${tecnico} concluiu sua ordem de serviço ${numeroOs}. Leva menos de 1 minuto para avaliar.`;
}

function buildServiceDetailCell(label, value, align, primary, accent = false) {
  const alignAttr = align === 'right' ? ' align="right"' : '';
  const tdStyle =
    align === 'right'
      ? 'vertical-align:top;text-align:right;width:50%;'
      : 'vertical-align:top;width:50%;';
  const labelStyle =
    'display:block;font-size:11px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;color:#94A3B8;margin:0 0 4px;';
  const valueColor = accent ? primary : '#0F172A';
  const valueStyle = `display:block;font-size:14px;font-weight:600;line-height:1.4;color:${valueColor};margin:0;`;
  const displayValue = String(value ?? '').trim() || '—';

  return `<td${alignAttr} style="${tdStyle}"><span style="${labelStyle}">${escapeHtml(label)}</span><span style="${valueStyle}">${escapeHtml(displayValue)}</span></td>`;
}

function buildSingleOrderDetailRows(sampleData, primary) {
  const rows = [
    [
      { label: 'Técnico', value: pickField(sampleData, 'tecnico') },
      { label: 'Ordem de serviço', value: pickField(sampleData, 'numero_os'), align: 'right', accent: true },
    ],
    [
      { label: 'Equipamento', value: pickField(sampleData, 'equipamento') },
      { label: 'Número de série', value: pickField(sampleData, 'numero_serie'), align: 'right' },
    ],
    [
      { label: 'Patrimônio', value: pickField(sampleData, 'patrimonio') },
      { label: 'Data do atendimento', value: pickField(sampleData, 'data_atendimento'), align: 'right' },
    ],
  ];

  return rows
    .map((pair, index) => {
      const divider =
        index > 0
          ? '<tr><td colspan="2" style="padding:12px 0 0;border-top:1px solid #E2E8F0;font-size:0;line-height:0;">&nbsp;</td></tr>'
          : '';
      const cells = pair
        .map(({ label, value, align = 'left', accent = false }) =>
          buildServiceDetailCell(label, value, align, primary, accent)
        )
        .join('');
      return `${divider}<tr>${cells}</tr>`;
    })
    .join('');
}

function buildMultiOrderServiceDetailsTable(multiOrders, primary) {
  const first = multiOrders[0] || {};
  const tecnico = pickField(first, 'tecnico');
  const dataAtendimento = pickField(first, 'data_atendimento');

  const orderRows = multiOrders
    .map((orderData, index) => {
      const bg = index % 2 === 0 ? '#FFFFFF' : '#F8FAFC';
      const numeroOs = pickField(orderData, 'numero_os');
      const equipamento = pickField(orderData, 'equipamento');
      const numeroSerie = pickField(orderData, 'numero_serie');
      const patrimonio = pickField(orderData, 'patrimonio');

      return `<tr>
                  <td bgcolor="${bg}" style="background:${bg};border-top:1px solid #F1F5F9;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td width="18%" style="padding:13px 0 13px 20px;font-size:13px;font-weight:700;color:${primary};">${escapeHtml(numeroOs)}</td>
                        <td width="32%" style="padding:13px 8px;font-size:13px;font-weight:600;color:#0F172A;line-height:1.4;">${escapeHtml(equipamento)}</td>
                        <td width="30%" style="padding:13px 8px;font-size:12px;color:#475569;line-height:1.4;">${escapeHtml(numeroSerie)}</td>
                        <td width="20%" style="padding:13px 20px 13px 8px;font-size:12px;color:#475569;">${escapeHtml(patrimonio)}</td>
                      </tr>
                    </table>
                  </td>
                </tr>`;
    })
    .join('');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #E2E8F0;border-radius:14px;overflow:hidden;">
                <tr>
                  <td bgcolor="#F8FAFC" style="background:#F8FAFC;padding:16px 20px;border-bottom:2px solid #E2E8F0;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td width="50%">
                          <p style="margin:0;font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#94A3B8;">Técnico</p>
                          <p style="margin:5px 0 0;font-size:14px;font-weight:700;color:#0F172A;">${escapeHtml(tecnico)}</p>
                        </td>
                        <td width="50%" align="right">
                          <p style="margin:0;font-size:10px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#94A3B8;">Data do atendimento</p>
                          <p style="margin:5px 0 0;font-size:14px;font-weight:700;color:#0F172A;">${escapeHtml(dataAtendimento)}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td bgcolor="#EEF2FF" style="background:#EEF2FF;padding:9px 0;border-bottom:1px solid #E2E8F0;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td width="18%" style="padding:0 0 0 20px;font-size:10px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:${primary};">OS</td>
                        <td width="32%" style="padding:0 8px;font-size:10px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:#64748B;">Equipamento</td>
                        <td width="30%" style="padding:0 8px;font-size:10px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:#64748B;">Nº de série</td>
                        <td width="20%" style="padding:0 20px 0 8px;font-size:10px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:#64748B;">Patrimônio</td>
                      </tr>
                    </table>
                  </td>
                </tr>
                ${orderRows}
              </table>`;
}

function buildServiceDetailsCard(sampleData, primary) {
  const multiOrders = Array.isArray(sampleData.orders) && sampleData.orders.length > 1
    ? sampleData.orders
    : null;

  if (multiOrders) {
    return buildMultiOrderServiceDetailsTable(multiOrders, primary);
  }

  const rows = buildSingleOrderDetailRows(sampleData, primary);
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#F8FAFC;border:1px solid #E2E8F0;border-radius:14px;">
                <tr>
                  <td style="padding:18px 22px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      ${rows}
                    </table>
                  </td>
                </tr>
              </table>`;
}

function buildFooterLinksBlock(footerLinks) {
  const raw = String(footerLinks ?? '').trim();
  if (!raw) return '';

  const urlPattern = /(https?:\/\/[^\s|]+)/gi;
  const urls = raw.match(urlPattern) || [];
  const labels = raw
    .split(/\||·/)
    .map((part) => part.replace(urlPattern, '').trim())
    .filter(Boolean);

  if (urls.length >= 2) {
    const labelA = labels[0] || 'Site';
    const labelB = labels[1] || 'Política de privacidade';
    return `<p style="margin:0;font-size:11px;color:#94A3B8;"><a href="${escapeHtml(urls[0])}" style="color:#94A3B8;text-decoration:underline;">${escapeHtml(labelA)}</a>&nbsp;·&nbsp;<a href="${escapeHtml(urls[1])}" style="color:#94A3B8;text-decoration:underline;">${escapeHtml(labelB)}</a></p>`;
  }

  if (urls.length === 1) {
    const label = labels[0] || 'Site';
    return `<p style="margin:0;font-size:11px;color:#94A3B8;"><a href="${escapeHtml(urls[0])}" style="color:#94A3B8;text-decoration:underline;">${escapeHtml(label)}</a></p>`;
  }

  const display = raw.replace(/\|/g, ' · ');
  return `<p style="margin:0;font-size:11px;color:#94A3B8;">${escapeHtml(display)}</p>`;
}

export function renderCxEmailTemplateHtml(template, sampleData = {}) {
  const merged = template?.visual && template?.content ? template : { visual: {}, content: {} };
  const visual = merged.visual || {};
  const content = merged.content || {};
  const fontFamily = FONT_STACKS[visual.fontFamily] || FONT_STACKS['Segoe UI'];
  const headerPadding = scaledHeaderPadding(visual.headerSize, visual.headerLogoScale);
  const align = visual.headerAlign || 'center';
  const primary = visual.primaryColor || '#4F46E5';
  const secondary = visual.secondaryColor || '#818CF8';
  const bg = visual.backgroundColor || '#F1F5F9';
  const btnBg = visual.buttonColor || primary;
  const btnText = visual.buttonTextColor || '#FFFFFF';
  const headerText = headerTextColor(primary, secondary);
  const btnShadow = hexToRgba(btnBg, 0.28);

  const greeting = replacePlaceholders(content.greeting, sampleData);
  const body = replacePlaceholders(content.body, sampleData);
  const buttonText = replacePlaceholders(content.buttonText, sampleData);
  const thankYou = replacePlaceholders(content.thankYou, sampleData);
  const signature = replacePlaceholders(content.signature, sampleData);
  const ratingLink = replacePlaceholders('{{link_avaliacao}}', sampleData);
  const previewText = buildPreviewText(sampleData);
  const serviceDetailsCard = buildServiceDetailsCard(sampleData, primary);

  const logoBlock = buildLogoBlock(visual, align, headerText);

  const footerLegalRaw = String(visual.footerLegal ?? '').trim();
  const footerLegalBase =
    footerLegalRaw && footerLegalRaw !== AUTO_FOOTER_MESSAGE
      && footerLegalRaw !== 'Este e-mail foi enviado automaticamente. Por favor, não responda diretamente a esta mensagem.'
      ? footerLegalRaw
      : '';
  const footerLegal = footerLegalBase ? replacePlaceholders(footerLegalBase, sampleData) : '';
  const footerLinksBlock = buildFooterLinksBlock(visual.footerLinks);
  const footerSignatureRaw = String(visual.footerSignature ?? '').trim();
  const footerSignature = footerSignatureRaw ? replacePlaceholders(footerSignatureRaw, sampleData) : '';

  const googleFont =
    visual.fontFamily === 'Roboto'
      ? '<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet" />'
      : visual.fontFamily === 'Inter'
        ? '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />'
        : '';

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta name="x-apple-disable-message-reformatting" />
  <meta name="color-scheme" content="light" />
  ${googleFont}
  <title>${escapeHtml(replacePlaceholders(content.subject, sampleData))}</title>
  <style>
    body, table, td { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { border: 0; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    body { margin: 0; padding: 0; width: 100% !important; height: 100% !important; overflow-x: hidden !important; }
    html { overflow-x: hidden !important; }
    .email-shell {
      width: 100% !important;
      max-width: 560px;
      margin: 0 auto;
      border-radius: ${EMAIL_CONTAINER_RADIUS} !important;
      -webkit-border-radius: ${EMAIL_CONTAINER_RADIUS} !important;
      overflow: hidden !important;
      background: #ffffff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 16px 40px rgba(15, 23, 42, 0.08);
      isolation: isolate;
      transform: translateZ(0);
    }
    .email-header-cell {
      border-radius: ${EMAIL_CONTAINER_RADIUS} ${EMAIL_CONTAINER_RADIUS} 0 0 !important;
      overflow: hidden !important;
    }
    .email-footer-cell {
      border-radius: 0 0 ${EMAIL_CONTAINER_RADIUS} ${EMAIL_CONTAINER_RADIUS} !important;
      overflow: hidden !important;
    }
    .email-container {
      width: 100% !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
      background: #ffffff;
    }
    @media screen and (max-width: 480px) {
      .email-shell { width: 100% !important; }
      .email-container { width: 100% !important; }
      .px-mobile { padding-left: 24px !important; padding-right: 24px !important; }
      .greeting { font-size: 20px !important; }
      .cta-link { display: block !important; }
      .stars { font-size: 20px !important; letter-spacing: 5px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background:${bg};font-family:${fontFamily};">
  <div style="display:none;max-height:0;overflow:hidden;opacity:0;">${escapeHtml(previewText)}</div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${bg};">
    <tr>
      <td align="center" style="padding:24px 10px;">
        <div class="email-shell" style="${emailShellStyle()}">
        <table role="presentation" class="email-container" width="100%" cellpadding="0" cellspacing="0" style="width:100%;background:#FFFFFF;border-collapse:collapse;border-spacing:0;">
          <tr>
            <td class="email-header-cell" align="${align}" bgcolor="${primary}" style="background-color:${primary};background-image:linear-gradient(135deg, ${primary} 0%, ${secondary} 100%);padding:${headerPadding};border-radius:${EMAIL_CONTAINER_RADIUS} ${EMAIL_CONTAINER_RADIUS} 0 0;">
              ${logoBlock}
            </td>
          </tr>
          <tr>
            <td class="px-mobile" align="center" style="padding:36px 40px 0;">
              <p class="greeting" style="margin:0;font-size:22px;line-height:1.3;font-weight:700;color:#0F172A;">${escapeHtml(greeting)}</p>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding:14px 40px 0;">
              <span class="stars" style="font-size:24px;line-height:1;letter-spacing:8px;color:#FBBF24;">★★★★★</span>
            </td>
          </tr>
          <tr>
            <td class="px-mobile" align="center" style="padding:16px 40px 0;">
              <p style="margin:0;font-size:15px;line-height:1.65;color:#475569;">${bodyToHtml(body)}</p>
            </td>
          </tr>
          <tr>
            <td class="px-mobile" style="padding:24px 40px 0;">
              ${serviceDetailsCard}
            </td>
          </tr>
          <tr>
            <td align="center" style="padding:30px 40px 4px;">
              <a href="${escapeHtml(ratingLink)}" class="cta-link" style="display:inline-block;padding:15px 38px;background:${btnBg};color:${btnText};text-decoration:none;border-radius:12px;font-size:15px;font-weight:600;letter-spacing:0.01em;box-shadow:0 8px 20px ${btnShadow};">${escapeHtml(buttonText)}</a>
              <p style="margin:14px 0 0;font-size:12px;color:#94A3B8;">Leva menos de 1 minuto</p>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding:26px 40px 32px;">
              <p style="margin:0;font-size:14px;line-height:1.6;color:#64748B;">${escapeHtml(thankYou)}</p>
              ${signature ? `<p style="margin:10px 0 0;font-size:14px;font-weight:600;color:${primary};">${escapeHtml(signature)}</p>` : ''}
            </td>
          </tr>
          <tr>
            <td style="padding:0 40px;">
              <div style="border-top:1px solid #E2E8F0;line-height:1px;font-size:1px;">&nbsp;</div>
            </td>
          </tr>
          <tr>
            <td class="email-footer-cell" align="center" style="padding:22px 40px 28px;background:#FFFFFF;border-radius:0 0 ${EMAIL_CONTAINER_RADIUS} ${EMAIL_CONTAINER_RADIUS};">
              ${footerLegal ? `<p style="margin:0 0 8px;font-size:11px;line-height:1.6;color:#94A3B8;">${escapeHtml(footerLegal)}</p>` : ''}
              ${footerLinksBlock}
              ${footerSignature ? `<p style="margin:12px 0 0;font-size:11px;color:#CBD5E1;">${escapeHtml(footerSignature)}</p>` : ''}
              <p style="margin:${footerSignature || footerLegal || footerLinksBlock ? '6px' : '0'} 0 0;font-size:11px;color:#CBD5E1;">${escapeHtml(AUTO_FOOTER_MESSAGE)}</p>
            </td>
          </tr>
        </table>
        </div>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
